/**
 * HT Artwork Selector v2.9.4 - PRODUCTION READY
 *
 * FIXES IN THIS VERSION:
 * - Fixed modal not reopening after close (complete solution)
 * - Event listeners properly cleaned up and re-attached on show()
 * - listenersSetup flag reset in cleanupEventListeners()
 * - show() method re-attaches listeners if needed
 * - Previous: Fixed duplicate listener registration, bound escape handler
 * - Modal show/hide works correctly with .is-active class
 *
 * @version 2.9.4
 */

'use strict';

class HTArtworkSelector {
  constructor(options = {}) {
    this.bridge = options.bridge;
    this.stateManager = options.stateManager;
    
    // Configuration
    this.DESIGN_SERVICE_PRICE = 25;
    this.TRANSITION_DURATION = 300;
    
    // DOM elements
    this.modal = null;
    this.modalBody = null;
    this.uploadWidget = null;
    this.uploadWidgetParent = null;
    
    // Structure detection
    this.modalStructureType = null; 
    this.isInitialized = false;
    
    // State management
    this.isVisible = false;
    this.selectedMethod = null;
    this.uploadedFiles = [];
    this.currentState = 'selection';

    // Event listener tracking
    this.listenersSetup = false;
    this.boundEscapeHandler = null;

    // Initialize
    this.init();
  }
  
  init() {
    this.waitForWizardModal(() => {
      this.setupArtworkInterface();
      this.isInitialized = true;
    });

    document.addEventListener('cart-bridge:success', (e) => {
      if (this.selectedMethod === 'upload_now' && this.uploadedFiles.length > 0) {
        this.dispatchUploadAnalytics();
      }
    });
  }
  
  findUploadWidget() {
    
    const filePondWidget = document.querySelector('.filepond--root');
    if (filePondWidget) {
      const uploadContainer = filePondWidget.closest('[data-upload-lift]') || 
                             filePondWidget.closest('.cl-upload--wrapper')?.parentElement;
      if (uploadContainer) {
        this.uploadWidget = uploadContainer;
        this.uploadWidgetParent = uploadContainer.parentNode;
        return true;
      }
    }
    
    console.warn('⚠️ No initialized Upload Lift widget found');
    return false;
  }

  waitForWizardModal(callback) {
  // Always create our own modal - don't use wizard's old one
  const existingModal = document.getElementById('ht-artwork-modal');
  if (existingModal) {
    existingModal.remove();
  }
  this.createNewModal();
  this.modalStructureType = 'container';
  callback();
}

  createNewModal() {
    const modalHTML = `
      <div id="ht-artwork-modal" class="ht-wizard-modal">
        <div class="ht-wizard-modal-overlay"></div>
        <div class="ht-wizard-modal-container">
          <div class="ht-wizard-modal-content">
            <div class="ht-wizard-modal-header">
              <h3>Choose Your Artwork Option</h3>
              <p>Select how you'd like to provide your artwork for printing.</p>
              <button type="button" class="ht-wizard-modal-close" aria-label="Close">×</button>
            </div>
            <div class="ht-wizard-modal-body">
              <!-- Content will be replaced by setupArtworkInterface -->
            </div>
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById('ht-artwork-modal');
    this.modalBody = this.modal.querySelector('.ht-wizard-modal-body');
    
  }
  
  setupArtworkInterface() {
    if (!this.modal || !this.modalBody) {
      console.error('Modal elements not found');
      return;
    }
    
    this.modalBody.innerHTML = this.createArtworkHTML();
    this.setupEventListeners();
    
  }
  
 
  createArtworkHTML() {
    return `
      <!-- Selection View -->
      <div class="ht-artwork-view" id="ht-artwork-selection-view">
        <div class="ht-artwork-options">
          
          <!-- Upload Now Option -->
          <div class="ht-artwork-option" data-method="upload_now">
            <div class="ht-artwork-option-icon" style="background:rgb(246, 162, 59);">
              <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
                <path d="M19.5 3h-9L6 7.5V21c0 .83.67 1.5 1.5 1.5h12c.83 0 1.5-.67 1.5-1.5V4.5c0-.83-.67-1.5-1.5-1.5zM19 21H8V8.5h3V5h8v16z"/>
                <path d="M12 18.5l-4-4h2.5v-5h3v5H16l-4 4z"/>
              </svg>
            </div>
            <div class="ht-artwork-option-content">
              <h4>Upload Now</h4>
              <p>Upload your artwork file immediately and proceed to checkout.</p>
              <div class="ht-artwork-option-benefits">✓ Fastest processing • ✓ Immediate confirmation</div>
            </div>
            <div class="ht-artwork-option-arrow">→</div>
          </div>
          
          <!-- Email Later Option -->
          <div class="ht-artwork-option" data-method="email_later">
            <div class="ht-artwork-option-icon" style="background: #10b981;">
              <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
                <path d="M2.5 3L22 12 2.5 21 2 13l13-1L2 11 2.5 3z"/>
              </svg>
            </div>
            <div class="ht-artwork-option-content">
              <h4>Email Later</h4>
              <p>Complete your order now and email us your artwork within 24 hours.</p>
              <div class="ht-artwork-option-benefits">✓ Quick checkout • ✓ Email artwork later</div>
            </div>
            <div class="ht-artwork-option-arrow">→</div>
          </div>
          
          <!-- Design Service Option -->
          <div class="ht-artwork-option" data-method="design_service">
            <div class="ht-artwork-option-icon" style="background: #8b5cf6;">
              <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
                <path d="M12 2C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm3 12.7V17h-6v-2.3l-1-1A5.02 5.02 0 0 1 7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.77-.93 3.47-2 4.7l-1 1z"/>
                <path d="M10.5 19h3v2h-3v-2zm0 3h3v1h-3v-1z"/>
                <circle cx="11" cy="9" r="1"/>
                <circle cx="13" cy="9" r="1"/>
              </svg>
            </div>
            <div class="ht-artwork-option-content">
              <h4>Design My Artwork Service</h4>
              <p>Our designers will create professional artwork based on your requirements.</p>
              <div class="ht-artwork-option-price">+£${(this.DESIGN_SERVICE_PRICE || 25).toFixed(2)} + VAT</div>
              <div class="ht-artwork-option-benefits">✓ Professional design • ✓ Revisions included</div>
            </div>
            <div class="ht-artwork-option-arrow">→</div>
          </div>
          
        </div>
      </div>
      
      <!-- Upload View -->
      <div class="ht-artwork-view" id="ht-artwork-upload-view" style="display: none;">
        <div class="ht-artwork-upload-header">
          <button type="button" class="ht-artwork-back-btn" id="ht-artwork-back-btn">
            ← Back to Options
          </button>
          <h3>Upload Your Artwork</h3>
          <p>Please upload your artwork file. Supported formats: AI, EPS, PDF, PSD, PNG, JPEG.</p>
        </div>
        
        <div class="ht-artwork-upload-container">
          <div id="ht-upload-lift-container">
            <!-- Upload widget will be moved here -->
          </div>
        </div>
        
        <div class="ht-artwork-upload-footer">
          <button type="button" class="ht-artwork-continue-btn" id="ht-artwork-continue-upload" disabled>
            Upload a file to continue
          </button>
        </div>
      </div>
      
      <!-- Confirmation View -->
      <div class="ht-artwork-view" id="ht-artwork-confirmation-view" style="display: none;">
        <div class="ht-artwork-confirmation-header">
          <button type="button" class="ht-artwork-back-btn" id="ht-artwork-change-btn">
            ← Back to Options
          </button>
        </div>
        <div class="ht-artwork-confirmation-content">
          <div class="ht-artwork-confirmation-icon">✓</div>
          <h3 id="ht-artwork-confirmation-title">Artwork Option Selected</h3>
          <p id="ht-artwork-confirmation-message">Your selection has been confirmed.</p>
          
          <div class="ht-artwork-confirmation-details" id="ht-artwork-confirmation-details">
            <!-- Dynamic content based on selection -->
          </div>
          
          <div class="ht-artwork-confirmation-actions">
            <button type="button" class="ht-artwork-proceed-btn" id="ht-artwork-proceed-btn">
              Proceed to Checkout
            </button>
          </div>
        </div>
      </div>
    `;
  }
  
  setupEventListeners() {
    // Prevent duplicate listener registration
    if (this.listenersSetup) {
      return;
    }


    this.modalBody.querySelectorAll('.ht-artwork-option').forEach(option => {
      option.addEventListener('click', (e) => {
        const method = option.getAttribute('data-method');
        this.selectArtworkMethod(method);
      });
    });

    const backBtn = document.getElementById('ht-artwork-back-btn');
    if (backBtn) {
      backBtn.addEventListener('click', () => {
        this.showSelectionView();
      });
    }

    const continueUploadBtn = document.getElementById('ht-artwork-continue-upload');
    if (continueUploadBtn) {
      continueUploadBtn.addEventListener('click', () => {
        this.proceedWithUpload();
      });
    }

    const changeBtn = document.getElementById('ht-artwork-change-btn');
    if (changeBtn) {
      changeBtn.addEventListener('click', () => {
        this.showSelectionView();
      });
    }

    const proceedBtn = document.getElementById('ht-artwork-proceed-btn');
    if (proceedBtn) {
      proceedBtn.addEventListener('click', () => {
        this.proceedToCart();
      });
    }

    const closeBtn = this.modal.querySelector('.ht-wizard-modal-close');
    const overlay = this.modal.querySelector('.ht-wizard-modal-overlay');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.cancel();
      });
    }

    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.cancel();
        }
      });
    }

    // Create bound handler so we can remove it later
    this.boundEscapeHandler = (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.cancel();
      }
    };

    document.addEventListener('keydown', this.boundEscapeHandler);

    this.listenersSetup = true;
  }
  
  async selectArtworkMethod(method) {
    this.selectedMethod = method;
    
    if (method === 'upload_now') {
      await this.showUploadView();
    } else if (method === 'email_later') {
      this.showEmailLaterConfirmation();
    } else if (method === 'design_service') {
      this.showDesignServiceConfirmation();
    }
  }
  
  async showUploadView() {
    
    await this.transitionToView('upload');
    
    const modalContainer = document.getElementById('ht-upload-lift-container');
    
    // Find and move the Upload Lift widget
    if (!this.uploadWidget) {
      this.findUploadWidget();
    }
    
    if (this.uploadWidget && modalContainer) {
      this.moveWidgetToModal(modalContainer);
      this.setupUploadEventListeners();
    } else {
      console.error('❌ No Upload Lift widget available');
      modalContainer.innerHTML = '<p style="color: #ef4444;">Upload widget not available. Please refresh the page and try again.</p>';
    }
  }
  
  moveWidgetToModal(container) {

    try {
      // Clear container and move widget
      container.innerHTML = '';
      container.appendChild(this.uploadWidget);

      // Disable continue button initially
      this.disableUploadContinue();

    } catch (error) {

      console.error('âŒ Failed to move widget:', error);
    }
  }
  
  setupUploadEventListeners() {
    
    const handleUploadAdded = (e) => {
      if (this.currentState === 'upload') {
        const file = e.detail;
        
        const variant = file.meta?.variant || 'original';
        
        if (variant === 'original') {
          const fileData = {
            url: file.url,
            originalUrl: file.url,
            thumbnailUrl: file.url,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.mimeType,
            uuid: file.uuid,
            path: file.path
          };
          
          this.uploadedFiles.push(fileData);
          
          this.enableUploadContinue();
          
        } else if (variant === 'thumbnail') {
          const lastFile = this.uploadedFiles[this.uploadedFiles.length - 1];
          if (lastFile && lastFile.uuid === file.uuid) {
            lastFile.thumbnailUrl = file.url;
          }
        }
      }
    };
    
    const handleUploadRemoved = (e) => {
      if (this.currentState === 'upload') {
        const file = e.detail;
        
        const index = this.uploadedFiles.findIndex(f => f.uuid === file.uuid);
        if (index !== -1) {
          this.uploadedFiles.splice(index, 1);
        }
        
        if (this.uploadedFiles.length === 0) {
          this.disableUploadContinue();
        } else {
          this.enableUploadContinue();
        }
      }
    };
    
    window.addEventListener('upload:added', handleUploadAdded);
    window.addEventListener('upload:removed', handleUploadRemoved);
    
    this.uploadEventListeners = {
      'upload:added': handleUploadAdded,
      'upload:removed': handleUploadRemoved
    };
  }
  
  /**
   * Enable upload continue button
   */
  enableUploadContinue() {
    const btn = document.getElementById('ht-artwork-continue-upload');
    if (btn) {
      btn.disabled = false;
      const count = this.uploadedFiles.length;
      btn.textContent = count === 1 
        ? 'Continue with 1 file' 
        : `Continue with ${count} files`;
    }
  }
  
  /**
   * Disable upload continue button
   */
  disableUploadContinue() {
    const btn = document.getElementById('ht-artwork-continue-upload');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Upload a file to continue';
    }
  }
  
  /**
   * Proceed with upload - SYNCHRONOUS TRANSFER & PROPER EVENT DISPATCH
   */
  proceedWithUpload() {
    
    // Validate files exist
    if (this.uploadedFiles.length === 0) {
      console.error('❌ No files to upload');
      alert('Please upload at least one file before continuing.');
      return;
    }
    
    // DIRECT SYNCHRONOUS TRANSFER to cart bridge
    this.bridge.uploadedFiles = [...this.uploadedFiles];
    
    // Dispatch artwork upload complete event
    document.dispatchEvent(new CustomEvent('artwork:upload-complete', {
      detail: {
        files: this.uploadedFiles,
        count: this.uploadedFiles.length
      }
    }));
    
    // Hide modal
    this.hide();
  }
  
  /**
   * Dispatch upload analytics events AFTER cart success
   */
  dispatchUploadAnalytics() {
    
    this.uploadedFiles.forEach((file, index) => {
      document.dispatchEvent(new CustomEvent('artwork:file-analytics', {
        detail: {
          uploadUrl: file.url,
          thumbnailUrl: file.thumbnailUrl,
          fileName: file.fileName,
          fileSize: file.fileSize,
          mimeType: file.mimeType,
          uuid: file.uuid,
          path: file.path,
          index: index,
          totalFiles: this.uploadedFiles.length
        }
      }));
    });
    
  }
  
  /**
   * Show email later confirmation
   */
  showEmailLaterConfirmation() {
    
    const title = document.getElementById('ht-artwork-confirmation-title');
    const message = document.getElementById('ht-artwork-confirmation-message');
    const details = document.getElementById('ht-artwork-confirmation-details');
    
    if (title) title.textContent = 'Email Artwork Later';
    if (message) message.textContent = 'You\'ve chosen to email your artwork after completing your order.';
    if (details) {
      details.innerHTML = `
        <div class="ht-artwork-confirmation-detail">
          <strong>Email Address:</strong> artwork@handytags.co.uk
        </div>
        <div class="ht-artwork-confirmation-detail">
          <strong>Timeline:</strong> Within 24 hours of order completion
        </div>
        <div class="ht-artwork-confirmation-detail">
          <strong>Reference:</strong> Include your order number in the email subject
        </div>
      `;
    }
    
    this.transitionToView('confirmation');
  }
  
  /**
   * Show design service confirmation
   */
  showDesignServiceConfirmation() {
    
    let designPrice = this.DESIGN_SERVICE_PRICE;
    if (window.htCalculator && window.htCalculator.pricingConstants) {
      designPrice = (window.htCalculator.pricingConstants.design_my_artwork || 2500) / 100;
    }
    
    const title = document.getElementById('ht-artwork-confirmation-title');
    const message = document.getElementById('ht-artwork-confirmation-message');
    const details = document.getElementById('ht-artwork-confirmation-details');
    
    if (title) title.textContent = 'Design My Artwork Service';
    if (message) message.textContent = 'Our professional designers will create your artwork.';
    if (details) {
      details.innerHTML = `
        <div class="ht-artwork-confirmation-detail">
          <strong>Service Fee:</strong> £${designPrice.toFixed(2)} + VAT
        </div>
        <div class="ht-artwork-confirmation-detail">
          <strong>Timeline:</strong> 2-3 business days for initial concepts
        </div>
        <div class="ht-artwork-confirmation-detail">
          <strong>Includes:</strong> Single design concepts and revisions
        </div>
      `;
    }
    
    this.transitionToView('confirmation');
  }
  
  /**
   * Transition between views
   */
  async transitionToView(targetView) {
    const currentView = document.getElementById(`ht-artwork-${this.currentState}-view`);
    const targetViewElement = document.getElementById(`ht-artwork-${targetView}-view`);
    
    if (!targetViewElement) return;
    
    if (currentView && currentView !== targetViewElement) {
      currentView.style.transition = `opacity ${this.TRANSITION_DURATION}ms ease`;
      currentView.style.opacity = '0';
      
      await new Promise(resolve => setTimeout(resolve, this.TRANSITION_DURATION));
      currentView.style.display = 'none';
    }
    
    targetViewElement.style.display = 'block';
    targetViewElement.style.opacity = '0';
    targetViewElement.style.transition = `opacity ${this.TRANSITION_DURATION}ms ease`;
    
    targetViewElement.offsetHeight;
    targetViewElement.style.opacity = '1';
    
    this.currentState = targetView;
  }
  
  /**
   * Show selection view
   */
  async showSelectionView() {
    
    this.selectedMethod = null;
    this.uploadedFiles = [];
    
    await this.transitionToView('selection');
  }
  
  /**
   * Proceed to cart
   */
  proceedToCart() {
    
    // If design service is selected, check the calculator checkbox to include the price
    if (this.selectedMethod === 'design_service') {
      const designCheckbox = document.getElementById('ht-design-my-artwork');
      if (designCheckbox && !designCheckbox.checked) {
        designCheckbox.checked = true;
        
        // Force calculator to recalculate with design service
        const activeCalculator = window.htCalculatorStyled || window.htCalculator;
        
        if (activeCalculator && activeCalculator.calculatePriceIfReady) {
          activeCalculator.calculatePriceIfReady();
          
          // Wait for recalculation, then dispatch events
          setTimeout(() => {
            // Update the cart bridge's data with the new price
            if (window.htCartBridge && window.htStateManager) {
              const updatedState = window.htStateManager.exportState();
              window.htCartBridge.currentCalculatorData = updatedState;
            }
            
            this.dispatchEvents();
          }, 300);
          
          return;
        }
      }
    }
    
    // For non-design-service, proceed immediately
    this.dispatchEvents();
  }

  /**
   * Dispatch events
   */
  dispatchEvents() {
    const eventDetail = {
      method: this.selectedMethod
    };
    
    
    document.dispatchEvent(new CustomEvent('artwork:method-selected', {
      detail: eventDetail
    }));
    
    this.hide();
  }
  
  /**
   * Cancel artwork selection
   */
  cancel() {
    document.dispatchEvent(new CustomEvent('artwork:cancelled'));
    this.hide();
  }
  
  /**
   * Show modal - CSS class control, no inline styles
   */
  show() {

    const hasEnhancedContent = this.modalBody?.querySelector('.ht-artwork-view');
    if (!hasEnhancedContent) {
      console.warn('⚠️ Modal not properly enhanced, forcing setup...');
      this.setupArtworkInterface();
    }

    // Ensure event listeners are set up (needed after cleanup on hide)
    if (!this.listenersSetup) {
      this.setupEventListeners();
    }

    // Reset state before showing
    this.selectedMethod = null;
    this.uploadedFiles = [];
    this.isVisible = true;
    this.currentState = 'selection';

    // Ensure all views are in clean state before showing
    const allViews = ['selection', 'upload', 'confirmation'];
    allViews.forEach(viewType => {
      const viewElement = document.getElementById(`ht-artwork-${viewType}-view`);
      if (viewElement) {
        viewElement.style.display = viewType === 'selection' ? 'block' : 'none';
        viewElement.style.opacity = viewType === 'selection' ? '1' : '0';
        viewElement.style.transition = '';
      }
    });

    if (this.modal) {
      // CSS class control - no inline styles needed
      this.modal.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }
  }

  /**
   * Hide modal - CSS class control
   */
  hide() {

    if (this.uploadWidget && this.uploadWidgetParent) {
      try {
        this.uploadWidgetParent.appendChild(this.uploadWidget);
      } catch (error) {
        console.error('Failed to return widget:', error);
      }
    }

    this.isVisible = false;

    if (this.modal) {
      // ✅ Pure CSS class control - CSS handles display: none
      this.modal.classList.remove('is-active');
      document.body.style.overflow = '';
    }

    // Reset all view states - clear inline styles from transitionToView()
    const allViews = ['selection', 'upload', 'confirmation'];
    allViews.forEach(viewType => {
      const viewElement = document.getElementById(`ht-artwork-${viewType}-view`);
      if (viewElement) {
        viewElement.style.display = '';
        viewElement.style.opacity = '';
        viewElement.style.transition = '';
      }
    });

    this.selectedMethod = null;
    this.uploadedFiles = [];
    this.currentState = 'selection';

    this.cleanupEventListeners();

  }
  
  /**
   * Cleanup event listeners
   */
  cleanupEventListeners() {
    // Remove upload widget listeners
    if (this.uploadEventListeners) {
      Object.entries(this.uploadEventListeners).forEach(([event, handler]) => {
        window.removeEventListener(event, handler);
      });
      this.uploadEventListeners = null;
    }

    // Remove global escape key listener
    if (this.boundEscapeHandler) {
      document.removeEventListener('keydown', this.boundEscapeHandler);
      this.boundEscapeHandler = null;
    }

    // Reset listeners flag so they can be set up again next time
    this.listenersSetup = false;

  }
  
  /**
   * Cleanup method
   */
  destroy() {
    this.cleanupEventListeners();
    
    if (this.modal && this.modal.id === 'ht-artwork-modal') {
      this.modal.remove();
    }
  }
}

// Global assignment
window.HTArtworkSelector = HTArtworkSelector;

// Export for ES modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HTArtworkSelector;
}